<template>
  <div>
    <div class="billing-detail-header">
      <span class="billing-detail-item">御利用月：{{ filteredMonthName }}</span>
      <span class="billing-detail-item">御法人名：{{ selectedCorporationName }}</span>
      <span class="billing-detail-item">項目：{{ billingItemName }}</span>
    </div>
    <button type="button" class="btn btn-gray" @click="exportToExcel">
      エクスポート
    </button>
    <button type="button" class="btn btn-gray" @click="exportToPdf">プリント</button>
    <button type="button" class="btn btn-gray" @click="displayColumns">表示項目選択</button>
    <div class="form-group float-right">
      <label for="text1">検索:</label>
      <input type="text" id="searchform_small" class="form-control" v-model="filter" />
    </div>

    <form class="form-inline showall">
      <div class="form-sort align-items-center">
        <div class="item-count col-2 my-1">
          <label class="mr-sm-2 sr-only" for="inlineFormCustomSelectSmall">
            Preference
          </label>
          <select class="custom-select mr-sm-2" id="inlineFormCustomSelectSmall" v-model="perPage">
            <option :value="totalBillingDetails" selected>全</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="150">150</option>
          </select>
          件表示
        </div>
      </div>
    </form>

    <div class="tab-content">
      <div id="table-1" class="tab-pane active">
        <div class="table-wrapper-scroll-y">
          <b-table
            ref="billing-detail-table"
            :min-width="tableWidth"
            class="sticky-table"
            selectable
            responsive
            sticky-header
            foot-clone
            no-footer-sorting
            :items="resourcesFiltered"
            :fields="filteredHeaders"
            :filter="filter"
            :filterIncludedFields="searchableFields"
            :per-page="perPage"
            :current-page="currentPage"
            :refreshed="tableRefreshed()"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                <b>{{ scope.label }}</b>
              </div>
            </template>

            <template v-slot:cell(status)="row">
              <div class="b-table-column-background-white">
                {{ row.value }}
              </div>
            </template>

            <template v-slot:cell(action)="row">
              <a class="hyper-text" @click="changeStatus(row)">
                判定修正
              </a>
            </template>

            <template v-slot:foot()="data">
              <div class="footFilters" v-show="data.field.footFilterable">
                <v-select
                  tag="div"
                  v-model="footFilters[data.column]"
                  placeholder="フィルタ"
                  class="select-control"
                  :options="tableFootOptions[data.column]"
                  :appendToBody="true"
                />
              </div>
            </template>
          </b-table>
        </div>
        <div class="pagination-footer">
          <label> {{ totalBillingDetails }} 件中 {{ baseIndex }} から {{ capIndex }} まで表示 </label>
          <div class="float-right">
            <button :disabled="!hasPreviousPage" type="button" class="btn btn-invisible" @click="goToPreviousPage">
              前
            </button>
            <label class="pagination-border text-center">{{ currentPage }}</label>
            <button :disabled="!hasNextPage" type="button" class="btn btn-invisible" @click="goToNextPage">
              次
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasAdminPrivilege" class="breakdown">
      <div>
        【教材別受講数】
        <ul>
          <li v-for="(value, key, index) in teachingMaterialBreakdown" :key="index">
            {{ key }}: <span>{{ value }}</span>
          </li>
        </ul>
        <div v-if="Object.keys(teachingMaterialUnbillableBreakdown).length > 0">
          【請求対象外】
          <ul>
            <li v-for="(value, key, index) in teachingMaterialUnbillableBreakdown" :key="index">
              {{ key }}: <span style="color: red;">- {{ value }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <PopUpHolder
      ref="popup_holder"
      no-close-on-esc
      no-close-on-backdrop
      green-button-title="OK"
      @greenClick="popupConfirmed"
    >
      <template v-slot:header>表示する項目を選択してください</template>
      <template v-slot:body>
        <thead>
          <tr v-for="field in customHeaders" :key="field.key">
            <th scope="row">
              <div class="custom-control custom-checkbox">
                <input type="checkbox" class="custom-control-input" :id="field.key" v-model="field.enable" />
                <label class="custom-control-label" :for="field.key">
                  {{ field.label }}
                </label>
              </div>
            </th>
          </tr>
        </thead>
      </template>
    </PopUpHolder>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { sumObjectArrayByKey } from '@/utils/math_helpers.js';
import PopUpHolder from '@/components/popups/PopUpHolder.vue';
import { updateStickyColumnsAndMinWidth } from '@/helpers/sticky_table';
import { resourcesFiltered, tableFootOptions } from '@/helpers/table';
import {
  basicJapaneseYearMonth,
  basicFieldPresent,
  basicPresent,
  basicBillingTypeIdDisplay,
  basicBillingTeachingMaterial400Display,
  basicBillingTeachingMaterial200Display,
  basicBillingTeachingMaterial100Display,
  basicBillingTeachingMaterial300Display,
  basicBillingTeachingMaterial700Display,
  basicBillingTeachingMaterial600Display,
  basicBillingTeachingMaterial500Display,
} from '@/presenters/fields';
import XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { buildJapaneseFont } from '@/helpers/PdfCustomFont.js';

export default {
  props: {
    filteredMonthName: {
      type: String,
      default: '',
    },
    selectedCorporationName: {
      type: String,
      default: '',
    },
    billingItem: {
      type: Object,
      default: null,
    },
  },
  components: {
    PopUpHolder,
  },
  data() {
    var fields = [
      {
        key: 'brand',
        enable: true,
        label: 'ブランド名',
        sortable: true,
        hidable: true,
        thAttr: { width: 150 },
        searchable: true,
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.brandNameDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.brandNameDisplay(value);
        },
      },
      {
        key: 'school',
        enable: true,
        label: '教場名',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.schoolNameDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.schoolNameDisplay(value);
        },
      },
      {
        key: 'studentId',
        enable: true,
        label: '生徒ID',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.studentIdDisplay(value);
        },
      },
      {
        key: 'name',
        enable: true,
        label: '生徒名',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.studentNameDisplay(value);
        },
      },
      {
        key: 'deactivatedDatetime',
        enable: true,
        label: '休会開始月',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.deactivatedDatetimeDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.deactivatedDatetimeDisplay(value);
        },
      },
      {
        key: 'numOfBooks',
        enable: true,
        label: '教材数',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 100 },
      },
      {
        key: 'teachingMaterial400',
        enable: true,
        label: '教材（図形の極）',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.teachingMaterial400Display(item.teachingMaterials);
        },
      },
      {
        key: 'teachingMaterial200',
        enable: true,
        label: '教材（数の極）',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.teachingMaterial200Display(item.teachingMaterials);
        },
      },
      {
        key: 'teachingMaterial100',
        enable: true,
        label: '教材（中学生の図形の極）',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.teachingMaterial100Display(item.teachingMaterials);
        },
      },
      {
        key: 'teachingMaterial300',
        enable: true,
        label: '教材（中学生の代数の極）',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.teachingMaterial300Display(item.teachingMaterials);
        },
      },
      {
        key: 'teachingMaterial700',
        enable: true,
        label: '教材（国語）',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.teachingMaterial700Display(item.teachingMaterials);
        },
      },
      {
        key: 'teachingMaterial600',
        enable: true,
        label: '教材（遊）',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.teachingMaterial600Display(item.teachingMaterials);
        },
      },
      {
        key: 'teachingMaterial500',
        enable: true,
        label: '教材（悟）',
        sortable: true,
        hidable: true,
        searchable: true,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.teachingMaterial500Display(item.teachingMaterials);
        },
      },
    ];
    if (this.$store.getters['role/hasAdminPrivilege']) {
      fields.push({
        key: 'status',
        enable: true,
        label: '判定',
        sortable: false,
        hidable: false,
        searchable: false,
        class: 'sticky-column right-sticky-column-2',
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.statusDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.statusDisplay(value);
        },
      });
      fields.push({
        key: 'action',
        enable: true,
        label: '',
        sortable: false,
        hidable: false,
        searchable: false,
        class: 'sticky-column right-sticky-column-1',
        thAttr: { width: 100 },
      });
    } else {
      fields.push({
        key: 'status',
        enable: true,
        label: '判定',
        sortable: false,
        hidable: false,
        searchable: false,
        class: 'sticky-column right-sticky-column-1',
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.statusDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.statusDisplay(value);
        },
      });
    }
    return {
      fields: fields,
      currentPage: 1,
      perPage: 50,
      selectAll: false,
      filter: null,
      footFilters: {},
    };
  },
  mounted() {},
  computed: {
    resources() {
      return this.billing.billingDetails;
    },
    resourcesFiltered() {
      return resourcesFiltered(this.resources, this.fields, this.footFilters);
    },
    tableFootOptions() {
      return tableFootOptions(this.resources, this.fields);
    },
    baseIndex() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    capIndex() {
      return Math.min(this.baseIndex + this.perPage - 1, this.totalBillingDetails);
    },
    hasPreviousPage() {
      return this.currentPage > 1;
    },
    hasNextPage() {
      return this.totalBillingDetails > this.currentPage * this.perPage;
    },
    customHeaders() {
      return this.fields.filter((field) => field.hidable == true);
    },
    filteredHeaders() {
      return this.fields.filter((field) => field.enable == true);
    },
    searchableFields() {
      var fields = this.fields.filter((field) => field.enable == true && field.searchable == true);
      return fields.map((field) => field.key);
    },
    tableWidth() {
      return sumObjectArrayByKey(this.filteredHeaders, 'thAttr', 'width') + 'px';
    },
    billingItemName() {
      if (this.billingItem) {
        return basicBillingTypeIdDisplay(this.billingItem.billingTypeId);
      }
      return '';
    },
    selectedFieldLabels() {
      const filteredFields = this.fields.filter(
        (field) => field.enable == true && field.label && field.key != 'status'
      );
      return filteredFields.map((field) => field.label);
    },
    teachingMaterialBreakdown() {
      const materialObj = {};
      for (const item of this.billingItem.items) {
        const materials = [];
        for (const material of item.teachingMaterials) {
          materials.push(material.name);
        }
        if (!materialObj[materials]) {
          materialObj[materials] = 1;
        } else {
          materialObj[materials] += 1;
        }
      }
      return materialObj;
    },
    teachingMaterialUnbillableBreakdown() {
      const materialUnbillableObj = {};
      for (const item of this.billingItem.items) {
        const materials = [];
        if (item.status == 'unbillable') {
          for (const material of item.teachingMaterials) {
            materials.push(material.name);
          }
          if (!materialUnbillableObj[materials]) {
            materialUnbillableObj[materials] = 1;
          } else {
            materialUnbillableObj[materials] += 1;
          }
        }
      }
      return materialUnbillableObj;
    },
    ...mapState(['billing']),
    ...mapGetters('billing', ['totalBillingDetails', 'getBillingDetailById', 'getExcelFormattedStudentBillingDetails']),
    ...mapGetters('role', ['hasAdminPrivilege']),
  },
  methods: {
    tableRefreshed() {
      this.$logger.debug('Table refreshed');
      if (this.hasAdminPrivilege) {
        updateStickyColumnsAndMinWidth({ leftColumns: 0, rightColumns: 2 });
      } else {
        updateStickyColumnsAndMinWidth({ leftColumns: 0, rightColumns: 1 });
      }
    },
    goToPreviousPage() {
      this.selectAll = false;
      this.currentPage--;
    },
    goToNextPage() {
      this.selectAll = false;
      this.currentPage++;
    },
    brandNameDisplay(brand) {
      return basicFieldPresent(brand, 'name');
    },
    schoolNameDisplay(school) {
      return basicFieldPresent(school, 'name');
    },
    studentIdDisplay(studentId) {
      return basicPresent(studentId);
    },
    studentNameDisplay(name) {
      return basicPresent(name);
    },
    deactivatedDatetimeDisplay(field) {
      return basicJapaneseYearMonth(field);
    },
    teachingMaterial400Display(field) {
      return basicBillingTeachingMaterial400Display(field);
    },
    teachingMaterial200Display(field) {
      return basicBillingTeachingMaterial200Display(field);
    },
    teachingMaterial100Display(field) {
      return basicBillingTeachingMaterial100Display(field);
    },
    teachingMaterial300Display(field) {
      return basicBillingTeachingMaterial300Display(field);
    },
    teachingMaterial700Display(field) {
      return basicBillingTeachingMaterial700Display(field);
    },
    teachingMaterial600Display(field) {
      return basicBillingTeachingMaterial600Display(field);
    },
    teachingMaterial500Display(field) {
      return basicBillingTeachingMaterial500Display(field);
    },
    statusDisplay(field) {
      switch (field) {
        case 'billable':
          return '請求対象';
        default:
          return '請求対象外';
      }
    },
    displayColumns() {
      this.$logger.debug('Display Columns for Student Billing Details');
      this.$refs['popup_holder'].show();
    },
    exportToExcel() {
      this.$logger.debug('Export Billing Details to Excel');
      let data = this.getExcelFormattedStudentBillingDetails(this.selectedFieldLabels);
      var billingWS = XLSX.utils.json_to_sheet(data);
      var wb = XLSX.utils.book_new(); // make Workbook of Excel
      XLSX.utils.book_append_sheet(wb, billingWS, 'Billing Details'); // add Worksheet to Workbook
      XLSX.writeFile(wb, 'billing_details.xlsx');
    },
    createHeaders(keys, columnWidth) {
      return keys.map((key) => ({
        name: key,
        prompt: key,
        width: columnWidth,
        align: 'center',
        padding: 0,
      }));
    },
    exportToPdf() {
      this.$logger.debug('Print Billing Details to PDF');
      let doc = new jsPDF({ orientation: 'l', format: 'a4' });
      buildJapaneseFont(doc);
      let data = this.getExcelFormattedStudentBillingDetails(this.selectedFieldLabels);
      const pdfPageWidth = doc.internal.pageSize.getWidth();
      const paddingWidth = 4;
      let columnWidth = (pdfPageWidth - paddingWidth * 2) / this.selectedFieldLabels.length;
      doc.table(paddingWidth, 4, data, this.createHeaders(this.selectedFieldLabels, columnWidth));
      doc.autoPrint();
      if (data.length > 150) {
        this.$logger.debug('Data too big, can only save');
        doc.save('billing_details.pdf', { returnPromise: true }).then(() => {
          this.$logger.debug('PDF saved successfully');
        });
      } else {
        this.$logger.debug('Data OK, Print from new window');
        doc.output('dataurlnewwindow', { filename: 'billing_details.pdf' });
      }
    },
    changeStatus(row) {
      this.$logger.debug('Change Billing Status with Id: ' + row.item.id);
      let billingDetail = this.getBillingDetailById(row.item.id);
      this.$emit('changeStudentBillingDetail', billingDetail);
    },
    popupConfirmed() {
      this.$logger.debug('Popup done');
    },
  },
};
</script>

<style scoped>
.billing-detail-header {
  margin-left: 20px;
  margin-bottom: 30px;
}
.billing-detail-item {
  margin-right: 20px;
}
.btn-sm {
  margin-left: 0px;
}
.btn-gray {
  margin-bottom: 5px;
}
.item-count {
  padding: 0;
  font-size: 10px;
}
.custom-control-label {
  font-weight: normal;
}
.pagination-footer {
  margin-top: 20px;
}
.hyper-text {
  color: #227afa;
  cursor: pointer;
}
.breakdown {
  margin-top: 10px;
  margin-bottom: -30px;
}
.breakdown span {
  font-weight: bold;
}
</style>
